import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { noIndexMeta } from "@/lib/seo";

type DeletionStatus = {
	confirmationCode: string;
	provider: string;
	status: "pending" | "completed" | "failed" | string;
	requestedAt: string | number;
	completedAt: string | number | null;
};

export const Route = createFileRoute("/data-deletion/$code")({
	component: DataDeletionStatusPage,
	head: () => ({
		meta: [{ title: "AgentReacher | Data deletion status" }, ...noIndexMeta()],
	}),
});

const PROVIDER_LABELS: Record<string, string> = {
	threads: "Threads",
	instagram: "Instagram",
	facebook: "Facebook",
};

function formatDate(value: string | number | null) {
	if (!value) return null;
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return null;
	return d.toLocaleString();
}

async function fetchDeletionStatus(code: string): Promise<DeletionStatus> {
	const res = await fetch(
		`/api/social/data-deletion/${encodeURIComponent(code)}`,
	);
	if (res.status === 404) {
		throw new Error("not_found");
	}
	if (!res.ok) {
		throw new Error("failed");
	}
	return (await res.json()) as DeletionStatus;
}

function DataDeletionStatusPage() {
	const { code } = Route.useParams();
	const query = useQuery({
		queryKey: ["data-deletion", code],
		queryFn: () => fetchDeletionStatus(code),
		retry: false,
	});

	return (
		<div className="flex min-h-screen items-center justify-center bg-background p-4">
			<div className="w-full max-w-md space-y-6">
				<div>
					<h1 className="text-xl font-semibold text-foreground">
						Data deletion request
					</h1>
					<p className="mt-2 text-sm text-muted-foreground">
						Status of your request to delete data associated with your connected
						social account.
					</p>
				</div>

				{query.isPending ? (
					<div className="rounded-md border border-border bg-card p-6 text-sm text-muted-foreground">
						Loading...
					</div>
				) : query.isError ? (
					<div className="rounded-md border border-border bg-card p-6 text-sm text-muted-foreground">
						{query.error instanceof Error && query.error.message === "not_found"
							? "We could not find a deletion request with that code."
							: "Failed to load deletion status."}
					</div>
				) : (
					<div className="space-y-3 rounded-md border border-border bg-card p-6 text-sm">
						<Row
							label="Status"
							value={
								query.data.status === "completed"
									? "Completed"
									: query.data.status === "failed"
										? "Failed"
										: "Pending"
							}
						/>
						<Row
							label="Provider"
							value={
								PROVIDER_LABELS[query.data.provider] ?? query.data.provider
							}
						/>
						<Row
							label="Confirmation code"
							value={query.data.confirmationCode}
						/>
						<Row
							label="Requested at"
							value={formatDate(query.data.requestedAt) ?? "Unknown"}
						/>
						{query.data.completedAt ? (
							<Row
								label="Completed at"
								value={formatDate(query.data.completedAt) ?? "Unknown"}
							/>
						) : null}
					</div>
				)}

				<p className="text-xs text-muted-foreground">
					AgentReacher removes all stored access tokens, profile metadata, and
					related publication records for the affected social account on receipt
					of a deletion request. If you have questions, see our{" "}
					<Link to="/privacy" className="underline">
						Privacy Policy
					</Link>
					.
				</p>
			</div>
		</div>
	);
}

function Row({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-start justify-between gap-4">
			<span className="text-muted-foreground">{label}</span>
			<span className="text-right font-medium text-foreground">{value}</span>
		</div>
	);
}
